import {Button, Modal} from 'antd'
import {member_joinAsMember_Api, member_leaveAsMember_Api} from 'apis'
import {getId, getType} from 'apis/model/base'
import DrawMenu from 'components/drawer/DrawMenu'
import {NullAsDefault, renderOwnChild, renderPhone} from "views/Shared";
import {UseHook} from 'components/UseHook'
import _ from 'lodash'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import Translate from 'modules/local/Translate'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext} from 'react'
import {IoExitOutline} from 'react-icons/io5'
import {RxEnter} from 'react-icons/rx'
import {useJoin} from 'views/Server/ServerContainer'
import {FollowButton, JoinButton, VoteButton} from '../custom/ToggleActionButton'
import {fromAppEntity} from '../enums'
import {bindQueryParams} from '../functions/routerHelper'
import {ConversationWith} from './createChatButton'
import {notifyOnError} from './createErrorEvent'
import {NotificationActionTypes, successNotify} from './createNotification'
import TooltipApp from "components/TooltipApp";
import {OrganizationShareWidget} from "./createShareWidget";
import {PiChatsFill, PiPhoneCallFill} from "react-icons/pi";
import {BsThreeDots} from "react-icons/bs";
import LoadingBlock from "../../Loading/LoadingBlock";

const UseJoinAction = ({id, type, children, join_status}) => {
    const handleJoin = useJoin({
        id,
        type,
        status: join_status,
    })

    return _.isFunction(children)
        ? children(handleJoin, {id, type})
        : children || null
}

export const useJoinAction = ({id, type, joined_status}) => {
    const t = useTranslate()
    const onError = notifyOnError(t)
    const onSuccess = () => {
        successNotify(NotificationActionTypes.INFORMATION, t)
    }
    const {handleAsyncAction: join} = useAsyncAction({
        onError,
        onSuccess,
        apiInfo: member_joinAsMember_Api,
        query: bindQueryParams([{id}, {prop: type}]),
    })
    const {handleAsyncAction: leave} = useAsyncAction({
        onError,
        onSuccess,
        apiInfo: member_leaveAsMember_Api,
        query: bindQueryParams([{id}, {prop: type}]),
    })
    return {
        id,
        type,
        join,
        leave,
        joined_status,
        toggle: () => {
            if (joined_status) {
                leave()
            } else {
                join()
            }
        },
    }
}

const ActionMemberJoin = (props) => (
    <Translate>
        {(t) => (
            <UseJoinAction
                join_status={false}
                {...props}>
                {(handleJoin) => (
                    <Button
                        type="primary"
                        onClick={handleJoin}
                        icon={<IoExitOutline/>}
                        className="flex gap-2 items-center">
                        {t('join')}
                    </Button>
                )}
            </UseJoinAction>
        )}
    </Translate>
)

const ActionMemberLeave = (props) => {
    const t = useTranslate()
    const showConfirmDialog = useCallback(
        (onOk) =>
            Modal.confirm({
                title: t('do you want to leave'),
                content: (
                    <span className="text-sm text-color-400 italic">
                        {t('you will no longer be a member of this community')}
                    </span>
                ),
                className: 'custom-modal',
                okText: t('sure'),
                cancelText: t('ignore'),
                okButtonProps: {
                    type: 'primary',
                    danger: true,
                    className: 'rounded-lg no-shadow no-text-shadow no-border',
                },
                cancelButtonProps: {
                    className: 'rounded-lg no-shadow no-text-shadow no-border text-color-200',
                },
                onOk,
            }),
        [t]
    )
    return (
        <UseJoinAction join_status={true} {...props}>
            {(handleLeave) => (
                <div
                    onClick={() => {
                        showConfirmDialog(handleLeave)
                    }}
                    className="flex items-center gap-3 hover:text-primary">
                    <IoExitOutline className="text-color-400"/>
                    <span className="font-medium text-sm text-color-100">{t('leave')}</span>
                </div>
            )}
        </UseJoinAction>
    )
}

const withToggleButton =
    ({renderLabel = (isToggle) => String(!!isToggle), ...props}) =>
        ({
             onClick,
             isToggle,
             active: active_button_params,
             inactive: inactive_button_params,
         }) => (
            <Button
                ghost={isToggle}
                onClick={onClick}
                type="primary"
                className="font-medium no-shadow no-text-shadow rounded-lg"
                {...props}>
                <span className="text-sm">{renderLabel(isToggle)}</span>
            </Button>
        )

const createStatusBar =
    (type, Context) =>
        ({canJoin = false, canVote = true, canFollow = true, Wrapper = renderOwnChild}) => {
            const t = useTranslate()

            const context = useContext(Context)

            const {contextName, ...rest} = fromAppEntity(type)

            const item = _.get(context, contextName)

            const {isLoading = false} = context || {}

            const contacts = _.get(item, 'contacts', [])

            let member = _.get(_.first(contacts), 'contact_member')
            let phone = _.get(_.first(contacts), 'phone')
            const joined_status = Boolean(_.get(item, 'joined_status'))

            const action_params = {
                id: getId(item),
                type: getType(item),
            }

            if (_.isEmpty(item)) return null

            return (
                <div className="mt-2 flex flex-col gap-3">
                    <div className="flex items-center gap-3">
                        <div className="flex gap-3 flex-1">
                            {canFollow && <FollowButton item={item}/>}
                            {canVote && <VoteButton item={item}/>}
                            {canJoin && !joined_status && <JoinButton {...action_params} item={item}/>}
                        </div>
                        {joined_status ? (
                            <DrawMenu
                                menu={
                                    {
                                        items: [
                                            {
                                                key: 'leave',
                                                label: <ActionMemberLeave {...action_params} />,
                                            },
                                        ],
                                        onClick: NullAsDefault,
                                    }
                                }
                                component={
                                    <div
                                        style={{
                                            width: 32,
                                            height: 32,
                                            cursor: 'pointer',
                                            borderRadius: '0.375rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                        className="border background-200 hover:background-300 text-color-400 hover:text-color-100 hover:shadow-xs">
                                        <BsThreeDots size={16} className="mx-auto"/>
                                    </div>
                                }>
                            </DrawMenu>
                        ) : (
                            false && (
                                <UseHook
                                    hook={useJoinAction}
                                    initialValue={action_params}>
                                    {({join = NullAsDefault}) => (
                                        <TooltipApp title={t('join')}>
                                            <div className="flex flex-center">
                                                <Button
                                                    type="primary"
                                                    block={true}
                                                    icon={<RxEnter/>}
                                                    onClick={() => {
                                                        join()
                                                    }}
                                                    variant={"solid"}
                                                    className="font-medium no-border"
                                                />
                                            </div>
                                        </TooltipApp>
                                    )}
                                </UseHook>
                            )
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        {
                            (type === 'organization' && contacts.length > 0)
                                ? (
                                    _.get(member, 'user') ? (
                                        <div className="grid grid-cols-2 gap-3 flex-1">
                                            {
                                                !!_.get(member, 'user')
                                                    ? (
                                                        <ConversationWith
                                                            params={{
                                                                receiver_id: getId(_.get(member, 'user')),
                                                            }}
                                                            render={(__, {onClick}) => (
                                                                <div
                                                                    onClick={onClick}
                                                                    className="flex flex-center bg-blue-100 hover:bg-blue-100 font-semibold text-blue-600 rounded-md cursor-pointer"
                                                                    style={{height: 32}}>
                                                                    <PiChatsFill size={16}/>
                                                                </div>
                                                            )}
                                                        />
                                                    ) : <div/>
                                            }
                                            {
                                                !!phone && (
                                                    renderPhone(
                                                        phone,
                                                        (value) => (
                                                            <div
                                                                style={{height: 32}}
                                                                className="flex flex-center bg-blue-100 hover:bg-blue-100 font-semibold text-blue-600 rounded-md cursor-pointer">
                                                                <PiPhoneCallFill size={16}/>
                                                            </div>
                                                        ),
                                                        ({href, children}) => (
                                                            <a
                                                                href={href}
                                                                className="no-underline">
                                                                {children}
                                                            </a>
                                                        )
                                                    )
                                                )}
                                        </div>
                                    ) : (
                                        <ConversationWith
                                            Wrapper={({children}) =>
                                                !!isLoading ? (
                                                    <LoadingBlock/>
                                                ) : (
                                                    <div className="flex flex-col">
                                                        <div
                                                            className="uppercase text-sm font-medium text-color-400 border-b mb-2">
                                                            {t('contact')}
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-3 flex-1">
                                                            {children}
                                                        </div>
                                                    </div>
                                                )
                                            }
                                            params={{
                                                receiver_id: getId(_.get(item, 'owner')),
                                            }}
                                            EmptyHolder={NullAsDefault}
                                        />
                                    )
                                )
                                : (
                                    <div/>
                                )
                        }

                        <OrganizationShareWidget className="justify-end gap-2"/>
                    </div>
                </div>
            )
        }

export default createStatusBar
